import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, Clock, Loader2, Plus, ShieldAlert, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Chip, Empty, Kpi, Loading, PageHeader, Panel, PrimaryButtonClass, Table, Td, Th } from "@/components/common/Chrome";
import { refOf, rowClick, useRowDetail } from "@/components/common/DetailSheet";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { dateTime, shortDate } from "@/lib/format";

const in90 = () => {
  const d = new Date();
  d.setDate(d.getDate() + 90);
  return d.toISOString().slice(0, 10);
};

const BLANK = {
  rule_key: "", justification: "", scope: "tenant", effect: "downgrade",
  evidence_ref: "", expires_on: in90(),
};

const STATUS_STYLE = {
  PENDING: "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300",
  ACTIVE: "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  EXPIRED: "border-border bg-muted text-muted-foreground",
};

const EFFECTS = [
  ["downgrade", "Downgrade severity to WARN"],
  ["suppress", "Suppress the rule entirely"],
];

export default function Exceptions({ embedded = false }) {
  const { can, user: me } = useAuth();
  const [rows, setRows] = useState(null);
  const detail = useRowDetail();
  const [rules, setRules] = useState([]);
  const [form, setForm] = useState(BLANK);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const [e, r] = await Promise.all([api.get("/regulatory/exceptions"), api.get("/regulatory/rules")]);
    setRows(e.data);
    setRules(r.data);
  }, []);

  useEffect(() => { load().catch(() => setRows([])); }, [load]);

  const counters = useMemo(() => {
    const list = rows || [];
    const today = new Date().toISOString().slice(0, 10);
    return {
      total: list.length,
      pending: list.filter((r) => r.status === "PENDING").length,
      active: list.filter((r) => r.status === "ACTIVE" && r.expires_on >= today).length,
      expired: list.filter((r) => r.expires_on < today).length,
    };
  }, [rows]);

  const request = async () => {
    setBusy("save");
    try {
      await api.post("/regulatory/exceptions", form);
      toast.success("Exception requested — awaiting a separate approver (FR-REG-009)");
      setOpen(false);
      setForm(BLANK);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy("");
  };

  const approve = async (row) => {
    setBusy(row.exception_id);
    try {
      await api.post(`/regulatory/exceptions/${row.exception_id}/approve`);
      toast.success(`${row.exception_id} approved and now active`);
      await load();
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy("");
  };

  if (!rows) return <Loading label="Loading tenant exceptions" />;
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="SCR-019 · Exception Requests"
        title="Time-boxed relief, with maker-checker separation"
        className={embedded ? "hidden" : ""}
        description="A compliance officer requests relief from a published rule with a justification, evidence reference and hard expiry. A different approver must accept it before the decision engine downgrades or suppresses the rule."
        actions={can("rule", "exception") && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-exception-button" className={PrimaryButtonClass}>
                <Plus className="mr-1.5 h-4 w-4" /> Request exception
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle>Request a rule exception</DialogTitle>
                <DialogDescription className="text-xs text-muted-foreground">
                  You cannot approve your own request — a second privileged reviewer must sign it off.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <F label="Rule">
                  <Select value={form.rule_key} onValueChange={(v) => setForm({ ...form, rule_key: v })}>
                    <SelectTrigger data-testid="exception-rule-select"><SelectValue placeholder="Select a rule" /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {rules.map((r) => (
                        <SelectItem key={r.id} value={r.rule_key}>{r.rule_key} · {r.title}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Effect">
                  <Select value={form.effect} onValueChange={(v) => setForm({ ...form, effect: v })}>
                    <SelectTrigger data-testid="exception-effect-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {EFFECTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </F>
                <F label="Justification">
                  <Textarea value={form.justification} rows={3}
                            onChange={(e) => setForm({ ...form, justification: e.target.value })}
                            data-testid="exception-justification-input"
                            placeholder="Why relief is needed, the compensating control, and who signed off internally." />
                </F>
                <F label="Evidence reference">
                  <Input value={form.evidence_ref} onChange={(e) => setForm({ ...form, evidence_ref: e.target.value })}
                         data-testid="exception-evidence-input" placeholder="EV-… / board minute reference" />
                </F>
                <F label="Expires on">
                  <Input type="date" value={form.expires_on}
                         onChange={(e) => setForm({ ...form, expires_on: e.target.value })}
                         data-testid="exception-expiry-input" className="font-mono" />
                </F>
              </div>
              <Button onClick={request} data-testid="exception-save-button" className={PrimaryButtonClass}
                      disabled={busy === "save" || !form.rule_key || form.justification.trim().length < 10}>
                {busy === "save" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Submit request
              </Button>
            </DialogContent>
          </Dialog>
        )}
      />

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <Kpi testId="kpi-exceptions-total" label="Exceptions on file" value={counters.total} icon={ShieldAlert} />
        <Kpi testId="kpi-exceptions-pending" label="Awaiting approval" value={counters.pending} accent="amber" icon={Clock} />
        <Kpi testId="kpi-exceptions-active" label="Active relief" value={counters.active} accent="emerald" icon={ShieldCheck} />
        <Kpi testId="kpi-exceptions-expired" label="Lapsed" value={counters.expired} accent="slate" />
      </div>

      <Panel testId="panel-exceptions" kicker="FR-REG-009" title="Exception register">
        <Table testId="exceptions-table"
               head={<><Th>Reference</Th><Th>Rule</Th><Th>Effect</Th><Th>Justification</Th><Th>Requested by</Th><Th>Approver</Th><Th>Expires</Th><Th>Status</Th><Th /></>}>
          {rows.length === 0 && <Empty colSpan={9} label="No exceptions requested" />}
          {rows.map((r) => {
            const lapsed = r.expires_on < today;
            const status = lapsed && r.status === "ACTIVE" ? "EXPIRED" : r.status;
            return (
              <tr key={refOf(r)} className="row-hover cursor-pointer" onClick={rowClick(() => detail.show(r, refOf(r)))} data-testid={`exception-row-${r.exception_id}`}>
                <Td className="whitespace-nowrap font-mono text-[11px]">{r.exception_id}</Td>
                <Td className="font-mono text-[11px] text-primary">{r.rule_key}</Td>
                <Td><Chip className="border-border bg-muted text-muted-foreground">{r.effect}</Chip></Td>
                <Td className="max-w-[220px] truncate text-xs text-muted-foreground" title={r.justification}>{r.justification}</Td>
                <Td className="font-mono text-[11px]">{r.requested_by}</Td>
                <Td className="font-mono text-[11px]">{r.approved_by || "—"}</Td>
                <Td className="whitespace-nowrap font-mono text-[11px]">{shortDate(r.expires_on)}</Td>
                <Td><Chip testId={`exception-status-${r.exception_id}`} className={STATUS_STYLE[status]}>{status}</Chip></Td>
                <Td>
                  {r.status === "PENDING" && can("rule", "approve") && (
                    <Button size="sm" variant="outline" onClick={() => approve(r)} disabled={busy === r.exception_id}
                            data-testid={`approve-exception-${r.exception_id}`}
                            className="h-7 rounded-full px-2.5 text-[10px]">
                      {busy === r.exception_id
                        ? <Loader2 className="h-3 w-3 animate-spin" />
                        : <><CheckCircle2 className="mr-1 h-3 w-3" /> Approve</>}
                    </Button>
                  )}
                  {r.status === "PENDING" && r.requested_by === me?.email && (
                    <div className="mt-1 text-[10px] text-amber-600 dark:text-amber-400">your request</div>
                  )}
                </Td>
              </tr>
            );
          })}
        </Table>
      </Panel>

      {rows.length > 0 && (
        <Panel testId="panel-exception-audit" kicker="Trail" title="Latest activity">
          <div className="divide-y divide-border">
            {rows.slice(0, 5).map((r) => (
              <div key={`${r.exception_id}-trail`} className="flex flex-wrap items-center gap-2 px-4 py-3 text-xs">
                <span className="font-mono text-[11px] text-primary">{r.exception_id}</span>
                <span className="text-muted-foreground">requested by {r.requested_by}</span>
                <span className="font-mono text-[10px] text-muted-foreground/70">{dateTime(r.created_at)}</span>
                {r.approved_at && (
                  <span className="text-emerald-600 dark:text-emerald-400">
                    approved by {r.approved_by} · {dateTime(r.approved_at)}
                  </span>
                )}
              </div>
            ))}
          </div>
        </Panel>
      )}

      {detail.sheet}
    </div>
  );
}

const F = ({ label, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11px] text-muted-foreground">{label}</Label>
    {children}
  </div>
);
